import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, MapPin, CheckCircle2, Truck, Clock, ShieldCheck } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { COMPANY } from "@/lib/company";
import {
  getProvinceBySlug,
  BRANCH_LABEL,
  REGION_LABEL,
  THAI_PROVINCES,
  type ThaiProvince,
} from "@/content/provinces-th/registry";
import { pickNearby } from "@/lib/nearby-links";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import {
  NOTARY_DOC_TYPES_20,
  NOTARY_COUNTRY_MATRIX,
  NOTARY_PRIORITY_TIERS,
} from "@/content/notary-knowledge";
import { NOTARY_KEYWORDS_TOP as NOTARY_KEYWORDS } from "@/content/notary-corpus/top.gen";

// Phase 16 — Province × Top-Service long-tail pages (77 × 4 = 308 pages).
// Captures queries like "notary public ขอนแก่น", "แปลรับรอง อุดรธานี",
// "ตรวจประวัติอาชญากร เชียงราย" — high-intent local search.

export interface CrossSellService {
  slug: string;
  label: string;
  labelEn: string;
  priceFrom: number;
  priceTo: number;
  unit: string;
  duration: string;
  desc: string;
  whoUses: string;
  parentServiceSlug: string;
}

export const PROVINCE_SERVICES: Record<string, CrossSellService> = {
  "notary-public": {
    slug: "notary-public",
    label: "Notary Public",
    labelEn: "Notary Public",
    priceFrom: 1500,
    priceTo: 8000,
    unit: "ต่อเอกสาร",
    duration: "1–2 วันทำการ",
    desc: "รับรองลายมือชื่อ สำเนาเอกสาร คำแปล Affidavit, POA โดยทนายความ Notarial Services Attorney ขึ้นทะเบียนสภาทนายความฯ — MFA และสถานทูตทุกประเทศยอมรับ",
    whoUses: "ผู้ที่ต้องการใช้เอกสารกับต่างประเทศ ยื่นวีซ่า สมัครงาน เปิดบัญชี ตั้งบริษัท หรือฟ้องคดี",
    parentServiceSlug: "notary-public",
  },
  "certified-translation": {
    slug: "certified-translation",
    label: "แปลรับรอง 30+ ภาษา",
    labelEn: "Certified Translation",
    priceFrom: 500,
    priceTo: 2500,
    unit: "ต่อหน้า",
    duration: "1–3 วันทำการ",
    desc: "แปลและรับรองเอกสารราชการ ทะเบียนบ้าน สูติบัตร ทะเบียนสมรส ปพ.1 ใบขับขี่ พร้อมรับรองทนาย / กงสุล / สถานทูต ครบจบในที่เดียว",
    whoUses: "ผู้ยื่นวีซ่า สมัครเรียนต่อต่างประเทศ ทำงานต่างประเทศ จดทะเบียนสมรสกับชาวต่างชาติ และธุรกิจส่งออก",
    parentServiceSlug: "translation-certified-all",
  },
  "mfa-consular": {
    slug: "mfa-consular",
    label: "MFA Consular Legalization",
    labelEn: "MFA Consular Legalization",
    priceFrom: 2500,
    priceTo: 5000,
    unit: "ต่อตราประทับ",
    duration: "3–5 วันทำการ",
    desc: "นำเอกสารไปรับรองที่กรมการกงสุล กระทรวงการต่างประเทศ (MFA) เพื่อใช้ในต่างประเทศ — รวมถึง Apostille ตั้งแต่ 28 ก.พ. 2570",
    whoUses: "ผู้ยื่นเอกสารใช้ในต่างประเทศ จดทะเบียนสมรสต่างประเทศ ทำงานต่างประเทศ และเรียนต่อ",
    parentServiceSlug: "mfa-consular",
  },
  "police-clearance": {
    slug: "police-clearance",
    label: "ตรวจประวัติอาชญากร",
    labelEn: "Police Clearance",
    priceFrom: 2000,
    priceTo: 8000,
    unit: "ต่อใบ",
    duration: "10–15 วันทำการ",
    desc: "ออกใบรับรองตรวจประวัติอาชญากรรมจากสำนักงานตำรวจแห่งชาติ (สตช.) — ภาษาไทย/อังกฤษ พร้อมแปลและรับรองกงสุล/สถานทูตสำหรับใช้ต่างประเทศ",
    whoUses: "ผู้สมัครวีซ่าทำงาน วีซ่าถาวร สมัครเรียนต่อต่างประเทศ จดทะเบียนสมรสต่างประเทศ และสมัครงานบริษัทต่างชาติ",
    parentServiceSlug: "police-clearance",
  },
};

export const PROVINCE_SERVICE_SLUGS = Object.keys(PROVINCE_SERVICES);

function buildFaqs(p: ThaiProvince, s: CrossSellService) {
  return [
    {
      q: `อยู่${p.nameTh} ใช้บริการ${s.label}ได้ไหม?`,
      a: `ได้ครับ — Thai Notary ให้บริการ${s.label}ครอบคลุม${p.nameTh}และทั่วประเทศ ผ่าน${BRANCH_LABEL[p.nearestBranch]} ส่งภาพเอกสารผ่านไลน์ ทีมประเมินและจัดส่งคืนผ่าน Kerry/Flash ภายใน${s.duration}`,
    },
    {
      q: `${s.label}ที่${p.nameTh} ราคาเท่าไหร่?`,
      a: `ราคาเริ่มต้น ${s.priceFrom.toLocaleString()} บาท${s.unit} สูงสุด ${s.priceTo.toLocaleString()} บาท ขึ้นกับประเภทเอกสารและจำนวนหน้า แจ้งราคารวมก่อนยืนยันงานเสมอ — ไม่มีค่าใช้จ่ายแอบแฝง`,
    },
    {
      q: `ต้องเดินทางจาก${p.nameTh}ไปออฟฟิศไหม?`,
      a: `ไม่จำเป็นสำหรับเอกสารส่วนใหญ่ — ส่งภาพผ่านไลน์/อีเมล ทีมดำเนินการที่${BRANCH_LABEL[p.nearestBranch]} แล้วส่งเอกสารต้นฉบับกลับ${p.nameTh}ผ่าน Kerry/Flash พร้อม tracking number`,
    },
    {
      q: `${s.label}จาก${p.nameTh}ใช้เวลานานไหม?`,
      a: `งานปกติใช้เวลา ${s.duration} หลังได้รับเอกสารครบ — เคสด่วน Same-Day ได้ที่${BRANCH_LABEL[p.nearestBranch]} แล้วส่ง Flash Express ในวันเดียวกัน (มีค่าด่วนเพิ่ม 30–50%)`,
    },
    {
      q: `ใครใน${p.nameTh}ที่ใช้บริการ${s.label}บ่อย?`,
      a: `${s.whoUses} — ทีมเราเข้าใจขั้นตอนของแต่ละสถานทูตและหน่วยงานต่างประเทศ`,
    },
    {
      q: `ค่าจัดส่งจาก${BRANCH_LABEL[p.nearestBranch]}ถึง${p.nameTh}เท่าไหร่?`,
      a: `ค่าจัดส่ง Kerry/Flash อยู่ที่ 50–150 บาท ขึ้นกับน้ำหนัก รวมประกันพัสดุและ tracking number — ส่งฟรีสำหรับเคสครบ 10,000 บาทขึ้นไป`,
    },
    {
      q: `${s.label}ที่${p.nameTh} ต้องเตรียมเอกสารอะไรบ้าง?`,
      a: `เอกสารหลักคือบัตรประชาชน + เอกสารที่ต้องการรับรอง/แปล — ทีมจะ checklist ส่งให้ตามเคสในไลน์หลังประเมินเอกสารชุดแรก`,
    },
    {
      q: `ออกใบกำกับภาษีสำหรับบริษัทใน${p.nameTh}ได้ไหม?`,
      a: `ได้ครับ — บริษัทจดทะเบียนถูกต้อง ออกใบกำกับภาษีเต็มรูปแบบ รองรับหัก ณ ที่จ่ายตามกฎหมาย ส่งทางอีเมลหรือไปรษณีย์`,
    },
  ];
}

export const Route = createFileRoute("/provinces/$province/$service")({
  loader: ({ params }): { province: ThaiProvince; service: CrossSellService } => {
    const p = getProvinceBySlug(params.province);
    const s = PROVINCE_SERVICES[params.service];
    if (!p || !s) throw notFound();
    return { province: p, service: s };
  },
  head: ({ params, loaderData }) => {
    const p = loaderData?.province ?? getProvinceBySlug(params.province);
    const s = loaderData?.service ?? PROVINCE_SERVICES[params.service];
    if (!p || !s) {
      return pageHeadForProvince({
        title: "ไม่พบหน้านี้",
        description: "ไม่พบหน้าบริการสำหรับจังหวัดนี้",
        path: `/provinces/${params.province}/${params.service}`,
      });
    }
    const site = getActiveSiteUrl();
    const title = `${s.label} ${p.nameTh} — ส่งถึงบ้านใน${s.duration} | Thai Notary`;
    const desc = `บริการ${s.label}สำหรับลูกค้า${p.nameTh} — เริ่ม ${s.priceFrom.toLocaleString()} บาท${s.unit} ส่งภาพไลน์ ทีม${BRANCH_LABEL[p.nearestBranch]}รับรองและจัดส่ง Kerry/Flash ภายใน${s.duration}`;
    const base = pageHeadForProvince({
      title,
      description: desc,
      path: `/provinces/${p.slug}/${s.slug}`,
      keywords:
        s.slug === "notary-public"
          ? [
              `notary public ${p.nameTh}`,
              `notary public ${p.nameEn}`,
              `รับรองเอกสาร ${p.nameTh}`,
              `Notarial Services Attorney ${p.nameEn}`,
              ...NOTARY_KEYWORDS.slice(0, 40),
            ]
          : undefined,
    });
    const faqs = buildFaqs(p, s);
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "77 จังหวัด", url: `${site}/provinces` },
              { name: p.nameTh, url: `${site}/provinces/${p.slug}` },
              { name: s.label, url: `${site}/provinces/${p.slug}/${s.slug}` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `${site}/provinces/${p.slug}/${s.slug}#service`,
            name: `${s.label} ${p.nameTh}`,
            alternateName: `${s.labelEn} in ${p.nameEn}`,
            description: s.desc,
            serviceType: s.labelEn,
            provider: {
              "@type": "LegalService",
              name: COMPANY.legalName,
              url: site,
              telephone: COMPANY.phone,
            },
            areaServed: {
              "@type": "AdministrativeArea",
              name: p.nameTh,
              alternateName: p.nameEn,
            },
            offers: {
              "@type": "AggregateOffer",
              priceCurrency: "THB",
              lowPrice: s.priceFrom,
              highPrice: s.priceTo,
              priceSpecification: {
                "@type": "UnitPriceSpecification",
                unitText: s.unit,
              },
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": `${site}/provinces/${p.slug}/${s.slug}#localbusiness`,
            name: `Thai Notary — ${s.label} ${p.nameTh}`,
            image: `${site}/og-image.jpg`,
            url: `${site}/provinces/${p.slug}/${s.slug}`,
            telephone: COMPANY.phone,
            priceRange: "฿฿",
            address: {
              "@type": "PostalAddress",
              addressLocality: p.nameEn,
              addressRegion: REGION_LABEL[p.region],
              addressCountry: "TH",
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: p.geo.lat,
              longitude: p.geo.lng,
            },
            areaServed: {
              "@type": "AdministrativeArea",
              name: p.nameTh,
              alternateName: p.nameEn,
            },
            openingHours: "Mo-Su 08:00-20:00",
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/provinces/${params.province}/${params.service}`)) },
      ],
    };
  },
  component: ProvinceServicePage,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold">ไม่พบหน้านี้</h1>
        <Link to="/provinces" className="mt-4 inline-flex items-center gap-1 text-gold">
          <ArrowLeft className="h-4 w-4" /> กลับรายการ 77 จังหวัด
        </Link>
      </div>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="p-10 text-center">
      <h1 className="text-xl font-bold">เกิดข้อผิดพลาด</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
    </div>
  ),
});

function ProvinceServicePage() {
  const data = Route.useLoaderData() as { province: ThaiProvince; service: CrossSellService };
  const { province: p, service: s } = data;
  const faqs = buildFaqs(p, s);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <nav className="border-b border-border bg-muted/30">
          <div className="mx-auto max-w-5xl px-4 py-3 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link>
            <span className="mx-2">/</span>
            <Link to="/provinces" className="hover:text-primary">77 จังหวัด</Link>
            <span className="mx-2">/</span>
            <Link to="/provinces/$province" params={{ province: p.slug }} className="hover:text-primary">{p.nameTh}</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">{s.label}</span>
          </div>
        </nav>

        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <div className="text-xs font-bold uppercase tracking-widest text-gold">
              <MapPin className="mr-1 inline h-3.5 w-3.5" />
              {REGION_LABEL[p.region]} · {p.nameTh}
            </div>
            <h1 className="mt-2 text-3xl font-extrabold leading-tight md:text-4xl">
              {s.label} {p.nameTh} — ส่งถึงบ้านใน{s.duration}
            </h1>
            <p id="ai-answer-tldr" className="mt-4 rounded-2xl border border-gold/30 bg-gold/5 p-5 text-base leading-relaxed">
              <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-gold">สรุปสั้น</span>
              อยู่{p.nameTh} ต้องใช้ <strong>{s.label}</strong>? Thai Notary ดูแลผ่าน{BRANCH_LABEL[p.nearestBranch]} —
              ส่งภาพเอกสารผ่านไลน์ ทีมประเมินภายใน 30 นาที ดำเนินการให้ภายใน{s.duration}
              ส่งคืน{p.nameTh}ผ่าน Kerry/Flash พร้อม tracking ราคาเริ่มต้น {s.priceFrom.toLocaleString()} บาท{s.unit}
            </p>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto grid max-w-5xl gap-3 px-4 py-8 md:grid-cols-4">
            <div className="rounded-xl border border-border bg-card p-4">
              <ShieldCheck className="h-5 w-5 text-gold" />
              <div className="mt-2 text-xs font-semibold uppercase text-muted-foreground">สาขาดูแล</div>
              <div className="mt-1 text-sm font-bold">{BRANCH_LABEL[p.nearestBranch]}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <Clock className="h-5 w-5 text-gold" />
              <div className="mt-2 text-xs font-semibold uppercase text-muted-foreground">ระยะเวลา</div>
              <div className="mt-1 text-sm font-bold">{s.duration}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <Truck className="h-5 w-5 text-gold" />
              <div className="mt-2 text-xs font-semibold uppercase text-muted-foreground">จัดส่ง</div>
              <div className="mt-1 text-sm font-bold">Kerry/Flash + tracking</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground">เริ่มต้น</div>
              <div className="mt-1 text-lg font-extrabold text-primary">฿{s.priceFrom.toLocaleString()}</div>
              <div className="text-[10px] text-muted-foreground">{s.unit}</div>
            </div>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-3xl px-4 py-12">
            <h2 className="text-2xl font-bold">{s.label}ใน{p.nameTh}คืออะไร?</h2>
            <p className="mt-4 text-base leading-relaxed">{s.desc}</p>
            <h3 className="mt-8 text-lg font-bold">ใครใน{p.nameTh}ใช้บริการนี้บ่อย?</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.whoUses}</p>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-2xl font-bold">ขั้นตอน 4 สเต็ป จาก{p.nameTh}ถึงเอกสารพร้อมใช้</h2>
            <ol className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {[
                { n: 1, t: "ส่งภาพเอกสารผ่านไลน์", d: `ทักไลน์ ${COMPANY.lineId} ส่งภาพถ่ายเอกสารหน้า-หลังให้ชัด` },
                { n: 2, t: "ทีมประเมิน + แจ้งราคา", d: `ทีม${BRANCH_LABEL[p.nearestBranch]}ประเมินภายใน 30 นาที แจ้งราคารวมและเวลาส่งคืน` },
                { n: 3, t: "ดำเนินการ + รับรอง", d: `ทีมรับรอง${s.label}ตามขั้นตอน — ใช้เวลา ${s.duration}` },
                { n: 4, t: `ส่ง${p.nameTh}ผ่าน Kerry/Flash`, d: "ส่งเอกสารต้นฉบับพร้อม tracking number ลูกค้าได้รับภายใน 1–3 วันทำการ" },
              ].map((step) => (
                <li key={step.n} className="rounded-2xl border border-border bg-card p-5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gold text-sm font-bold text-foreground">{step.n}</div>
                  <h3 className="mt-3 text-base font-bold">{step.t}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{step.d}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-2xl font-bold">ทำไมลูกค้า{p.nameTh}เลือก Thai Notary สำหรับ{s.label}</h2>
            <ul className="mt-4 grid gap-3 md:grid-cols-2">
              {[
                `ทีมทนาย Notarial Services Attorney ขึ้นทะเบียนสภาทนายความฯ — MFA และสถานทูตทุกประเทศยอมรับ`,
                `${BRANCH_LABEL[p.nearestBranch]}ดูแลโซน${p.nameTh} — ตอบไลน์ภายใน 30 นาที (08:00–20:00 ทุกวัน)`,
                `ราคาตายตัวก่อนยืนยันงาน — ไม่มีค่าใช้จ่ายแอบแฝง`,
                `รองรับเคสด่วน Same-Day ที่${BRANCH_LABEL[p.nearestBranch]} ส่ง Flash Express ในวันเดียวกัน`,
                `${COMPANY.trustLine} — ใบเสร็จ/ใบกำกับภาษีเต็มรูปแบบ`,
                `รับประกันงาน — หากเอกสารถูกปฏิเสธจากหน่วยงานปลายทาง ทีมแก้ไขให้ฟรี`,
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-2 rounded-xl border border-border bg-card p-4 text-sm">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="defer-paint border-b border-border">
          <div className="mx-auto max-w-3xl px-4 py-12">
            <h2 className="text-2xl font-bold">คำถามที่พบบ่อย — {s.label} {p.nameTh}</h2>
            <div className="mt-5 space-y-3">
              {faqs.map((f, i) => (
                <details key={i} className="group rounded-xl border border-border bg-card p-4">
                  <summary className="cursor-pointer font-semibold text-primary">{f.q}</summary>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {s.slug === "notary-public" && (
          <>
            <section className="defer-paint border-b border-border bg-muted/20">
              <div className="mx-auto max-w-5xl px-4 py-12">
                <h2 className="text-2xl font-bold">
                  20 ประเภทเอกสารที่ลูกค้า{p.nameTh}ใช้ Notary Public บ่อย
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  ทีมทนาย Notarial Services Attorney ที่ดูแล{p.nameTh}ผ่าน{BRANCH_LABEL[p.nearestBranch]} รับรองเอกสารทุกประเภทตามมาตรฐาน MFA + สถานทูต — ตัวอย่างที่พบบ่อยที่สุด 20 รายการ
                </p>
                <div className="mt-6 grid gap-2 md:grid-cols-2">
                  {NOTARY_DOC_TYPES_20.map((d) => (
                    <div key={d.en} className="rounded-lg border border-border bg-card p-3 text-sm">
                      <div className="font-semibold text-primary">{d.th}</div>
                      <div className="text-xs text-muted-foreground">{d.en} — {d.note}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="defer-paint border-b border-border">
              <div className="mx-auto max-w-5xl px-4 py-12">
                <h2 className="text-2xl font-bold">
                  Country Recognition Matrix — เอกสารจาก{p.nameTh}ใช้ได้ที่ประเทศไหน
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  หลัง MFA เปิด Apostille 28 ก.พ. 2570 — เอกสาร Notary + Apostille ใช้ได้ตรง 130 ประเทศภาคี HCCH โดยไม่ต้องผ่านสถานทูตอีก
                </p>
                <div className="mt-6 overflow-x-auto">
                  <table className="w-full min-w-[560px] text-sm">
                    <thead className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="py-2 pr-3">ประเทศ</th>
                        <th className="py-2 pr-3">ช่องทาง</th>
                        <th className="py-2">หมายเหตุ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {NOTARY_COUNTRY_MATRIX.map((c) => (
                        <tr key={c.code} className="border-b border-border/60">
                          <td className="py-2 pr-3 font-semibold">{c.name}</td>
                          <td className="py-2 pr-3">
                            <span
                              className={
                                c.chain === "apostille"
                                  ? "rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-bold text-emerald-600"
                                  : c.chain === "embassy"
                                    ? "rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-bold text-amber-600"
                                    : "rounded-full bg-blue-500/10 px-2 py-0.5 text-[11px] font-bold text-blue-600"
                              }
                            >
                              {c.chain === "apostille" ? "Apostille" : c.chain === "embassy" ? "Embassy" : "Hybrid"}
                            </span>
                          </td>
                          <td className="py-2 text-xs text-muted-foreground">{c.note}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            <section className="defer-paint border-b border-border bg-muted/20">
              <div className="mx-auto max-w-5xl px-4 py-12">
                <h2 className="text-2xl font-bold">
                  Priority Scheduling — เลือกความเร่งด่วนได้จาก{p.nameTh}
                </h2>
                <div className="mt-6 grid gap-3 md:grid-cols-2 lg:grid-cols-5">
                  {NOTARY_PRIORITY_TIERS.map((t) => (
                    <div key={t.code} className="rounded-xl border border-border bg-card p-4">
                      <div className="text-xs font-bold text-gold">{t.code}</div>
                      <div className="mt-1 text-sm font-bold">{t.name}</div>
                      <div className="mt-2 text-xs text-muted-foreground">SLA: {t.sla}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{t.useCase}</div>
                      <div className="mt-2 text-xs font-semibold text-primary">{t.surchargePct}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </>
        )}

        <section className="defer-paint border-b border-border bg-muted/20">
          <div className="mx-auto max-w-5xl px-4 py-10">
            <h2 className="text-xl font-bold">บริการอื่นใน{p.nameTh}</h2>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              {PROVINCE_SERVICE_SLUGS.filter((slug) => slug !== s.slug).map((slug) => {
                const other = PROVINCE_SERVICES[slug];
                return (
                  <Link
                    key={slug}
                    to="/provinces/$province/$service"
                    params={{ province: p.slug, service: slug }}
                    className="rounded-xl border border-border bg-card p-4 transition-all hover:-translate-y-0.5 hover:border-gold"
                  >
                    <div className="text-sm font-bold text-primary">{other.label} {p.nameTh}</div>
                    <div className="mt-1 text-xs text-muted-foreground">เริ่ม ฿{other.priceFrom.toLocaleString()} · {other.duration}</div>
                  </Link>
                );
              })}
              <Link
                to="/services/$service"
                params={{ service: s.parentServiceSlug }}
                className="rounded-xl border border-gold/30 bg-gold/5 p-4 transition-all hover:-translate-y-0.5 hover:border-gold"
              >
                <div className="text-sm font-bold text-gold">รายละเอียด {s.label} ทั้งหมด →</div>
                <div className="mt-1 text-xs text-muted-foreground">ราคา ขั้นตอน เอกสาร FAQ ครบ</div>
              </Link>
            </div>
          </div>
        </section>

        <section className="defer-paint border-b border-border">
          <div className="mx-auto max-w-5xl px-4 py-10">
            <h2 className="text-xl font-bold">
              {s.label} ในจังหวัดใกล้เคียง {p.nameTh} ({REGION_LABEL[p.region]})
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              เราส่งเอกสารด่วนทั่วภูมิภาคเดียวกันภายใน 1 วันทำการ ถ้าคุณอยู่จังหวัดข้างเคียง
              เปิดหน้าจังหวัดของคุณได้เลย หรือดู
              <Link to="/provinces" className="mx-1 font-semibold text-primary hover:underline">
                รายชื่อบริการครบ 77 จังหวัด
              </Link>
              และ
              <Link to="/bangkok" className="mx-1 font-semibold text-primary hover:underline">
                บริการรายเขตในกรุงเทพฯ
              </Link>
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              {pickNearby(THAI_PROVINCES, p, (x) => x.region, 5).map((n) => (
                <Link
                  key={n.slug}
                  to="/provinces/$province/$service"
                  params={{ province: n.slug, service: s.slug }}
                  className="rounded-xl border border-border bg-card p-4 transition-all hover:-translate-y-0.5 hover:border-gold"
                >
                  <div className="text-sm font-bold text-primary">{s.label} {n.nameTh}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {REGION_LABEL[n.region]} · {n.nameEn}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-4xl px-4 py-12 text-center">
            <h2 className="text-2xl font-extrabold">พร้อมเริ่ม{s.label}ที่{p.nameTh}แล้ว?</h2>
            <p className="mt-3 text-sm opacity-90">
              ทักไลน์ส่งภาพเอกสาร — ทีมประเมินภายใน 30 นาที พร้อมแจ้งเวลาส่งคืน{p.nameTh}
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <a href={`tel:${COMPANY.phoneIntl}`} className="rounded-md bg-gold px-5 py-2.5 text-sm font-bold text-foreground">
                โทร {COMPANY.phone}
              </a>
              <a href={`https://line.me/R/ti/p/${COMPANY.lineId}`} target="_blank" rel="noopener" className="rounded-md border border-primary-foreground/30 bg-primary-foreground/10 px-5 py-2.5 text-sm font-bold">
                ทักไลน์ {COMPANY.lineId}
              </a>
            </div>
          </div>
        </section>
        <div className="mx-auto max-w-4xl px-4 pb-12">
          <FaqSourceNote topic="notary" />
        </div>

      </main>
      <SiteFooter />
    </div>
  );
}